// Settings.jsx
import React, { useState, useEffect, useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  Tabs as MuiTabs,
  Tab,
  Breadcrumbs,
  Link as MuiLink,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";

import OrgSettings from "./OrgSettings.jsx";
import Centres from "./Centres.jsx";
import ManagePeoples from "./ManagePeoples.jsx";
import DeveloperSettings from "./DeveloperSettings.jsx";
import InterpretationCredits from "../../components/Credits/InterpretationCredits.jsx";
import { AuthContext } from "../../context/AuthContextProvider";

const Settings = () => {
  const { id } = useParams(); // tab from URL
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const defaultCenterCount = JSON.parse(localStorage.getItem("@centre_stats"));
  const defaultPeopleCount = JSON.parse(localStorage.getItem("@people_stats"));
  // default center count will calculater by center count
  // Role checker
  const hasRole = (roles) => roles.includes(user?.userType);
  // Migrated businesses don't get Manage Members / Developer Settings - userType
  // stays plain "b2b", migration is a separate flag.
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;

  // Default tab based only on role (NOT on id)
  const defaultTab = useMemo(() => {
    const savedTab = sessionStorage.getItem("settingsTab");

    const allowedTabs = [
      ...(hasRole(["b2b"]) ? ["account"] : []),
      ...(hasRole(["b2b"]) && !isMigratedUser ? ["webhook"] : []),
      ...(hasRole(["b2b"]) && !isMigratedUser ? ["credits"] : []),
      ...(hasRole(["b2b", "b2b-admin"]) ? ["centres"] : []),
      ...(isMigratedUser ? [] : ["members"]),
    ];

    if (savedTab && allowedTabs.includes(savedTab)) {
      return savedTab;
    }

    if (hasRole(["b2b"])) return "account";
    if (hasRole(["b2b-admin"])) return "centres";
    return isMigratedUser ? "account" : "members";
  }, [user]); // depends on user/roles

  // Active tab = URL param if present, otherwise default
  const activeKey = id || defaultTab;

  const [centersTotal, setCentersTotal] = useState(null);
  const [peoplesTotal, setPeoplesTotal] = useState(null);

  // If user visits /settings without :id, redirect once to default tab
  useEffect(() => {
    if (!id && defaultTab) {
      navigate(`/settings/${defaultTab}`, { replace: true });
    }
  }, [id, defaultTab, navigate]);

  // Breadcrumb Title
  const getBreadcrumbTitle = () => {
    switch (activeKey) {
      case "account":
        return hasRole(["b2b"]) ? "Organisation Settings" : "";
      case "centres":
        return hasRole(["b2b", "b2b-admin"]) ? "Manage Centres" : "";
      case "members":
        return "Manage Members";
      case "webhook":
        return hasRole(["b2b"]) && !isMigratedUser ? "Developers Settings" : "";
      case "credits":
        return hasRole(["b2b"]) && !isMigratedUser ? "Interpretation & Credits" : "";
      default:
        return "";
    }
  };

  const centersLabel =
    centersTotal == null
      ? `Manage Centres (${defaultCenterCount?.totalCount ?? 0})`
      : `Manage Centres (${centersTotal})`;
  const peoplesLabel =
    peoplesTotal === null || peoplesTotal === 0
      ? `Manage Members (${defaultPeopleCount?.totalCount})`
      : `Manage Members (${peoplesTotal})`;

  return (
    <>
      <Helmet>
        <title>Spandan - Settings</title>
      </Helmet>

      <Box className="main-wrappers">
        <Box
          className="page-wrapper"
          sx={{
            pb: "10px",

            maxHeight: "100vh",
            overflowY: "auto",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <Box className="content">
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs separator="/" sx={{ mb: 1 }}>
                {/* use RouterLink instead of plain href to avoid full reload */}
                <MuiLink
                  underline="hover"
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Figtree",
                    color: "var(--app-text)",
                    fontWeight: 400,
                    fontStyle: "regular",
                  }}
                  component={RouterLink}
                  to="/settings"
                >
                  Settings
                </MuiLink>

                {getBreadcrumbTitle() && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Figtree",
                      color: "var(--app-text)",
                      fontWeight: 600,
                      fontStyle: "semi-bold",
                    }}
                  >
                    {getBreadcrumbTitle()}
                  </Typography>
                )}
              </Breadcrumbs>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontFamily: "Figtree",
                  color: "var(--app-text)",
                  fontSize: "20px",
                  fontStyle: "Bold",
                }}
              >
                Settings
              </Typography>
            </Box>

            <Box className="fixed-tab-wrapper">
              <MuiTabs
                value={activeKey}
                onChange={(e, v) => {
                  sessionStorage.setItem("settingsTab", v);
                  navigate(`/settings/${v}`);
                }} // navigate on tab click
                variant="scrollable"
                scrollButtons="none"
                aria-label="settings tabs"
                textColor="inherit"
                indicatorColor="primary"
                TabIndicatorProps={{
                  style: {
                    height: "3px",
                    backgroundColor: "#5751E9",
                  },
                }}
                sx={{
                  "& .MuiTab-root": {
                    color: "var(--app-text)",
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 400,
                    minWidth: "auto",
                    paddingRight: "25px",
                    paddingLeft: "25px",
                    fontFamily: "Figtree",
                  },
                  "& .Mui-selected": {
                    fontWeight: 700,
                    color: "var(--app-text)",
                  },
                }}
              >
                {hasRole(["b2b"]) && (
                  <Tab value="account" label="Organisation Settings" />
                )}

                {hasRole(["b2b", "b2b-admin"]) && (
                  <Tab value="centres" label={centersLabel} />
                )}

                {!isMigratedUser && (
                  <Tab value="members" label={peoplesLabel} />
                )}
                {hasRole(["b2b"]) && !isMigratedUser && (
                  <Tab value="webhook" label="Developer Settings" />
                )}

                {hasRole(["b2b"]) && !isMigratedUser && (
                  <Tab value="credits" label="Interpretation & Credits" />
                )}
              </MuiTabs>

              <Divider sx={{ bgcolor: "var(--app-border)", height: "1px", mt: "0px" }} />

              <Box sx={{ mt: 2 }}>
                {activeKey === "webhook" && hasRole(["b2b"]) && !isMigratedUser && (
                  <DeveloperSettings />
                )}

                {activeKey === "credits" && hasRole(["b2b"]) && !isMigratedUser && (
                  <InterpretationCredits />
                )}

                {activeKey === "account" && hasRole(["b2b"]) && <OrgSettings />}

                {activeKey === "centres" && hasRole(["b2b", "b2b-admin"]) && (
                  <Centres onTotalChange={setCentersTotal} />
                )}

                {activeKey === "members" && !isMigratedUser && (
                  <ManagePeoples onTotalChange={setPeoplesTotal} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Settings;
